import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { 
    Activity, 
    Mail, 
    Loader2, 
    AlertCircle,
    CheckCircle2,
    ArrowLeft,
    ArrowRight,
    Shield
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const BackIcon = isRTL ? ArrowRight : ArrowLeft;
    
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await axios.post(`${API}/auth/forgot-password`, { email });
            setSubmitted(true);
            toast.success(t('forgotPassword.checkEmail'));
        } catch (err) {
            // Still show success to not reveal if email exists
            setSubmitted(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 via-white to-sky-50/30" data-testid="forgot-password-page">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="flex justify-center mb-8">
                    <Link to="/" className="flex items-center gap-3 group">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1E3A5F] to-[#0D2137] flex items-center justify-center shadow-xl">
                            <Activity className="w-7 h-7 text-teal-400" />
                        </div>
                        <div>
                            <span className="text-xl font-bold text-slate-800">{t('app.name')}</span>
                            <span className="block text-xs text-slate-500">{t('app.subtitle')}</span>
                        </div>
                    </Link>
                </div>

                <Card className="border-0 shadow-2xl shadow-slate-200/50">
                    <CardContent className="p-8">
                        {!submitted ? (
                            <>
                                <div className="text-center mb-8">
                                    <div className="w-16 h-16 rounded-2xl bg-teal-50 flex items-center justify-center mx-auto mb-4">
                                        <Mail className="w-8 h-8 text-teal-600" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-slate-800 mb-2">{t('forgotPassword.title')}</h2>
                                    <p className="text-slate-500">
                                        {t('forgotPassword.subtitle')}
                                    </p>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-5">
                                    {error && (
                                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                            {error}
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <Label htmlFor="email" className="text-slate-700">{t('auth.email')}</Label>
                                        <div className="relative">
                                            <Mail className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <Input
                                                id="email"
                                                type="email"
                                                placeholder={t('auth.enterEmail')}
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                                className="ps-10 h-12 rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500/20"
                                                data-testid="forgot-email-input"
                                            />
                                        </div>
                                    </div>

                                    <Button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full h-12 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium shadow-lg shadow-teal-500/25"
                                        data-testid="forgot-submit-btn"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="me-2 w-5 h-5 animate-spin" />
                                                {t('forgotPassword.sending')}
                                            </>
                                        ) : (
                                            t('forgotPassword.sendResetLink')
                                        )}
                                    </Button>
                                </form>
                            </>
                        ) : (
                            <div className="text-center py-6">
                                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                                </div>
                                <h3 className="text-xl font-semibold text-slate-800 mb-2">{t('forgotPassword.checkEmail')}</h3>
                                <p className="text-slate-500 mb-6" dangerouslySetInnerHTML={{ __html: t('forgotPassword.emailSent', { email }) }} />
                                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 text-sm text-amber-700 mb-6">
                                    <Shield className="w-4 h-4 inline me-2" />
                                    {t('forgotPassword.linkExpiry')}
                                </div>
                                <Button
                                    variant="outline"
                                    onClick={() => setSubmitted(false)}
                                    className="rounded-xl"
                                >
                                    {t('forgotPassword.tryDifferent')}
                                </Button>
                            </div>
                        )}

                        <div className="mt-6 text-center">
                            <Link to="/login" className="inline-flex items-center gap-2 text-teal-600 font-medium hover:underline">
                                <BackIcon className="w-4 h-4" />
                                {t('forgotPassword.backToSignIn')}
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
